// Shared helpers for turning the backend /plans response into display cards.
// The API is the single source of truth for pricing — no hardcoded prices.

export interface ApiPlan {
  id: string;
  name: string;
  description?: string | null;
  price_monthly: string | number;
  price_yearly?: string | number | null;
  credits: number | string;
  validity_days?: number;
  features?: string[] | null;
  is_active?: boolean;
}

export interface PlanCard {
  id: string;
  name: string;
  price: string;        // e.g. "$29"
  priceValue: number;   // 29
  credits: number;      // 100
  creditsLabel: string; // "100 credits"
  perCredit: string;    // "$0.29"
  savingsLabel?: string;// "save 65%"
  features: string[];
  featured: boolean;    // best value (lowest per-credit)
}

const money = (n: number): string =>
  Number.isInteger(n) ? `$${n.toLocaleString('en-US')}` : `$${n.toFixed(2)}`;

/**
 * Build sorted, display-ready plan cards from the raw /plans payload.
 * Derives per-credit rate, a "save X%" badge vs the most expensive per-credit
 * plan, and flags the best-value plan as featured.
 */
export function buildPlanCards(plans: ApiPlan[]): PlanCard[] {
  const rows = (plans ?? [])
    .map((p) => ({
      raw: p,
      priceValue: parseFloat(String(p.price_monthly)) || 0,
      creditsNum: Number(p.credits) || 0,
    }))
    .filter((p) => p.creditsNum > 0 && p.priceValue > 0)
    .sort((a, b) => a.priceValue - b.priceValue);

  if (rows.length === 0) return [];

  const perCreditOf = (p: (typeof rows)[number]) => p.priceValue / p.creditsNum;
  const worst = Math.max(...rows.map(perCreditOf)); // baseline (most expensive per credit)
  const best = Math.min(...rows.map(perCreditOf));  // best value

  return rows.map((p) => {
    const pc = perCreditOf(p);
    const savingsPct = worst > 0 ? Math.round((1 - pc / worst) * 100) : 0;
    const feats = p.raw.features && p.raw.features.length > 0
      ? p.raw.features
      : [
          `${p.creditsNum.toLocaleString()} credits`,
          'Every contact found per search',
          'Name and job title',
          'CSV export',
        ];

    return {
      id: p.raw.id,
      name: p.raw.name,
      price: money(p.priceValue),
      priceValue: p.priceValue,
      credits: p.creditsNum,
      creditsLabel: `${p.creditsNum.toLocaleString()} credits`,
      perCredit: pc < 0.1 ? `$${pc.toFixed(3)}` : `$${pc.toFixed(2)}`,
      savingsLabel: savingsPct > 0 ? `save ${savingsPct}%` : undefined,
      features: feats,
      featured: pc === best && rows.length > 1,
    };
  });
}
